/*
 * O composer do Flarum com a forma do composer do Canela:
 *
 *   alça · "Iniciar Discussão" / "Tópico ▾" ............ «  ⤢  —  ✕
 *   categoria ▾                 | 🏷 etiquetas opcionais ▾
 *   Título
 *   texto                       | prévia
 *   B I H 🔗 " </> … 😀 ............ Descartar  [Criar tópico]
 *
 * O core continua dono do comportamento (abrir, redimensionar, minimizar,
 * tela cheia, enviar); aqui só se trocam os itens das ItemLists e as classes
 * — a disposição é do CSS (`less/forum/Composer.less`).
 *
 * Composer, DiscussionComposer, ReplyComposer e EditPostComposer vêm num pedaço
 * carregado sob demanda: são estendidos pelo nome do módulo.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import ComposerState from 'flarum/forum/states/ComposerState';
import TextEditor from 'flarum/common/components/TextEditor';
import Button from 'flarum/common/components/Button';
import ComposerActionTitle from './ComposerActionTitle';
import ComposerFields from './ComposerFields';
import { missingTagsMessage, normalizeTags, selectableTags } from './tagRules';
import installModals from './installModals';
import installTagModal from './tagModal';
import installAlignComposer from './alignComposer';
import installMobileViewport from './mobileViewport';
import installMentionsDropdown from './mentionsDropdown';
import installCaret from './caret';
import { isMorphing, morphComposer } from './morph';
import installToolbar, { addMoreAction, richMode, setPlaceholderProvider } from './installToolbar';
import { editorComposer } from '../features/composer/common';
import { draftableBody, draftsOn, saveCurrentDraft } from '../features/composer/drafts/fofDrafts';
import { previewToggle, splitPossible, splitPreviewVisible } from '../features/composer/installPreview';
import { isExtensionEnabled, t } from '../utils/settings';
const REPLY = 'flarum/forum/components/ReplyComposer';
const EDIT = 'flarum/forum/components/EditPostComposer';
const DISCUSSION = 'flarum/forum/components/DiscussionComposer';

/** As alturas iniciais: resposta 255px, o resto 400px. */
function defaultHeight() {
  return app.composer.bodyMatches(REPLY) ? 255 : 400;
}

/** A pessoa já arrastou a alça: a altura dela vale para todos os composers. */
function hasStoredHeight() {
  try {
    return !!localStorage.getItem('composerHeight');
  } catch {
    return false;
  }
}
function kindClass() {
  if (app.composer.bodyMatches(REPLY)) return 'CanelaComposer--reply';
  if (app.composer.bodyMatches(EDIT)) return 'CanelaComposer--edit';
  if (app.composer.bodyMatches(DISCUSSION)) return 'CanelaComposer--newTopic';
  return 'CanelaComposer--other';
}

/** Põe o conteúdo numa chave (trocando o que houver) com a prioridade dada. */
function put(items, key, content, priority) {
  if (items.has(key)) items.setContent(key, content).setPriority(key, priority);else items.add(key, content, priority);
}
function controlButton(icon, label, onclick, className = '') {
  return m(Button, {
    className: `Button Button--icon Button--link CanelaComposer-control ${className}`,
    icon: icon,
    title: label,
    "aria-label": label,
    onclick: onclick
  });
}

/**
 * O ✕ é "Salvar e fechar": com rascunhos, guarda o texto e fecha
 * sem perguntar. Sem rascunhos, fecha como o core (que pergunta se há texto).
 */
function saveAndClose() {
  if (draftsOn() && draftableBody()) {
    saveCurrentDraft();
    app.composer.hide();
  } else {
    app.composer.close();
  }
}

/**
 * O placeholder, que muda com o modo: no "A", "Digite aqui. Use
 * a barra de ferramentas ou Markdown para formatar."; no Markdown, "Digite
 * aqui. Use Markdown, BBCode, ou HTML para formatar." — aqui só com os
 * formatos ligados neste fórum. "Arraste ou cole imagens." só com o
 * fof/upload, que é quem recebe o arrastar e o colar.
 */
function bodyPlaceholder(rich = richMode()) {
  const formats = [isExtensionEnabled('flarum-markdown') && 'Markdown', isExtensionEnabled('flarum-bbcode') && 'BBCode'].filter(Boolean);
  const upload = isExtensionEnabled('fof-upload') && !!app.forum.attribute('fof-upload.canUpload');
  const or = ` ${t('composer.shell.or')} `;
  let text;
  if (rich && formats.includes('Markdown')) text = t('composer.shell.body_placeholder_markdown');else if (formats.length) text = t('composer.shell.body_placeholder_formats', {
    formats: formats.join(or)
  });else text = t('composer.shell.body_placeholder');
  return upload ? `${text} ${t('composer.shell.drag_images')}` : text;
}

/**
 * Os rótulos ("Iniciar Discussão", "Responder", "Salvar edição") e os
 * placeholders, só quando quem abriu o composer não passou os seus.
 */
function installLabels() {
  const labels = [['forum/components/DiscussionComposer', 'composer.shell.submit_new_topic'], ['forum/components/ReplyComposer', 'composer.shell.submit_reply'], ['forum/components/EditPostComposer', 'composer.shell.submit_edit']];
  labels.forEach(([id, submitKey]) => {
    flarum.reg.onLoad('core', id, module => {
      override(module, 'initAttrs', function (original, attrs) {
        attrs.submitLabel = attrs.submitLabel || t(submitKey);
        attrs.placeholder = attrs.placeholder || bodyPlaceholder();
        if (id.endsWith('DiscussionComposer')) attrs.titlePlaceholder = attrs.titlePlaceholder || t('composer.shell.title_placeholder');
        return original(attrs);
      });
    });
  });
}
function installHeights() {
  override(ComposerState.prototype, 'minimumHeight', () => 255);

  // O Composer pega a altura padrão uma vez, ao montar; os composers abertos
  // depois herdam a do anterior. Aqui ela é decidida a cada abertura.
  override(ComposerState.prototype, 'load', async function (original, ...args) {
    const result = await original(...args);
    if (!hasStoredHeight() && this.body?.componentClass) this.height = defaultHeight();
    return result;
  });
  override('flarum/forum/components/Composer', 'defaultHeight', () => defaultHeight());

  // Durante a troca de forma (tela cheia, prévia), a caixa está no meio do
  // caminho: medida agora, a área de texto ficaria com a altura antiga e o
  // rodapé fora do lugar. As alturas certas já foram gravadas antes do
  // movimento, e o morph manda medir de novo no fim.
  override('flarum/forum/components/Composer', 'updateHeight', function (original) {
    if (!isMorphing()) original();
  });
}

/** As classes de forma da última vez que o composer estava aberto (ver installRoot). */
let shapeClasses = '';
function installRoot() {
  // As classes de que o CSS precisa: o tipo do composer e se a prévia está
  // ao lado (`show-preview` / `hide-preview`, que mudam a largura).
  extend('flarum/forum/components/Composer', 'view', function (vdom) {
    if (!vdom?.attrs) return;

    // Ao fechar, o core esvazia o composer antes de ele descer: recalculadas,
    // as classes viravam as de um composer vazio (outro tipo, sem prévia) e a
    // caixa pulava de lado e encolhia no meio da descida. Fechando, ficam as
    // da última vez que ele estava aberto.
    if (app.composer.position === 'hidden' && shapeClasses) {
      vdom.attrs.className = `${vdom.attrs.className} CanelaComposer ${shapeClasses}`;
      return;
    }
    const rich = richMode();
    shapeClasses = `${kindClass()} ${splitPreviewVisible() && !rich ? 'show-preview' : 'hide-preview'}${rich ? ' is-rich' : ' is-markdown'}`;
    vdom.attrs.className = `${vdom.attrs.className} CanelaComposer ${shapeClasses}`;
  });

  // «, tela cheia, minimizar e ✕ — a ordem e os ícones do canto. Minimizado,
  // só o ✕, sobre a barra colorida.
  extend('flarum/forum/components/Composer', 'controlItems', function (items) {
    const state = app.composer;
    const phone = app.screen() === 'phone';
    const closeLabel = draftsOn() && draftableBody() ? t('composer.shell.save_and_close') : app.translator.trans('core.forum.composer.close_tooltip', {}, true);
    if (state.position === 'minimized') {
      put(items, 'close', controlButton('fas fa-xmark', closeLabel, saveAndClose), 10);
      return;
    }

    // Os ícones do canto: tela cheia, minimizar e fechar — e, no
    // modo Markdown, o « da prévia antes deles. O que outras extensões ponham
    // aqui sai.
    Object.keys(items.toObject()).forEach(key => {
      if (!['fullScreen', 'minimize', 'close'].includes(key)) items.remove(key);
    });
    if (splitPossible()) put(items, 'canelaPreview', previewToggle(), 40);
    if (!phone) {
      put(items, 'fullScreen', state.position === 'fullScreen' ? controlButton('fas fa-compress', app.translator.trans('core.forum.composer.exit_full_screen_tooltip', {}, true), () => morphComposer(() => state.exitFullScreen())) : controlButton('fas fa-expand', app.translator.trans('core.forum.composer.full_screen_tooltip', {}, true), () => morphComposer(() => state.fullScreen())), 30);
    }

    // Sem o `App-backControl` do core, que no celular vira o "voltar" do
    // cabeçalho: o composer móvel mostra — e ✕ na própria linha.
    put(items, 'minimize', controlButton('fas fa-minus', app.translator.trans('core.forum.composer.minimize_tooltip', {}, true), () => state.minimize()), 20);
    put(items, 'close', controlButton('fas fa-xmark', closeLabel, saveAndClose), 10);
  });
}

/**
 * O cabeçalho. Entra no primeiro corpo de composer, depois de todas as
 * extensões: assim os itens que elas põem (as tags, a enquete do fof/polls)
 * já estão na lista quando são trocados.
 */
function installHeader() {
  // A enquete do fof/polls: o botão "Adicionar enquete" do cabeçalho vira um
  // item "Criar enquete" do menu ⊕ da barra.
  addMoreAction(() => {
    const poll = app.composer.canelaPoll;
    if (!poll || !app.composer.isVisible()) return null;
    return {
      key: 'poll',
      icon: 'fas fa-square-poll-horizontal',
      label: poll.hasPoll ? t('composer.shell.edit_poll') : t('composer.shell.add_poll'),
      active: poll.hasPoll,
      onclick: poll.onclick
    };
  });
  const takePoll = (body, items) => {
    const poll = items.has('polls') ? items.get('polls') : null;
    app.composer.canelaPoll = poll?.attrs?.onclick ? {
      onclick: poll.attrs.onclick,
      hasPoll: !!body.composer?.fields?.poll
    } : null;
    if (poll) items.remove('polls');
  };
  let applied = false;
  extend('flarum/forum/components/ComposerBody', 'oninit', function () {
    if (applied) return;
    applied = true;
    extend(DISCUSSION, 'headerItems', function (items) {
      put(items, 'title', m(ComposerActionTitle, {
        kind: "new_topic",
        title: this.title
      }), 1000);

      // O botão do flarum/tags vira a linha "categoria | etiquetas".
      if (items.has('tags')) {
        const original = items.get('tags');
        const disabled = String(original?.attrs?.className || '').includes('disabled') || !!this.attrs.disabled;

        // As tags que chegam de fora (a página da tag, um rascunho, o tópico
        // vinculado) passam pelas regras do flarum/tags: sem filha órfã e sem
        // tag em que o ator não pode iniciar discussão.
        const current = this.composer.fields.tags || [];
        const allowed = normalizeTags(current.filter(tag => tag?.canStartDiscussion?.() !== false));
        if (allowed.length !== current.length) this.composer.fields.tags = allowed;
        put(items, 'tags', m(ComposerFields, {
          tags: allowed,
          disabled: disabled,
          onchange: tags => {
            this.composer.fields.tags = tags;
          }
        }), 500);
      }
      takePoll(this, items);
    });
    extend(REPLY, 'headerItems', function (items) {
      put(items, 'title', m(ComposerActionTitle, {
        kind: "reply",
        discussion: this.attrs.discussion
      }), 1000);
      takePoll(this, items);
    });
    extend(EDIT, 'headerItems', function (items) {
      put(items, 'title', m(ComposerActionTitle, {
        kind: "edit",
        post: this.attrs.post
      }), 1000);
      takePoll(this, items);
    });
  });
}

/**
 * Os mínimos de tags do flarum/tags (`minPrimaryTags`, `minSecondaryTags`)
 * cobrados ao publicar, com a mensagem do próprio flarum/tags, e o seletor
 * que falta preencher aberto. Sem isso o flarum/tags abriria o modal dele,
 * que o tema substitui pela linha do composer. Fica por fora do override do
 * flarum/tags (que inicializa antes): só chega a ele uma seleção válida.
 */
function installTagRequirements() {
  override(DISCUSSION, 'onsubmit', function (original, ...args) {
    if (!isExtensionEnabled('flarum-tags')) return original(...args);
    const tags = normalizeTags(this.composer.fields.tags || []);
    this.composer.fields.tags = tags;
    const missing = selectableTags().length ? missingTagsMessage(tags) : null;
    if (!missing) return original(...args);
    app.alerts.show({
      type: 'error'
    }, missing.message);
    const cell = missing.kind === 'primary' ? 'category' : 'tags';
    const chooser = document.querySelector(`.CanelaComposerFields-${cell}:not(.is-expanded) .CanelaSelectKit-header`);
    chooser?.click();
    return undefined;
  });
}
function installFooter() {
  // O rodapé: a barra de formatação à esquerda e, à direita,
  // "Descartar" e o botão primário sem ícone.
  extend(TextEditor.prototype, 'controlItems', function (items) {
    if (!editorComposer(this) || editorComposer(this) !== app.composer) return;
    const attrs = this.attrs;

    // O "olho" de prévia do core: o tema já mostra a prévia lado a lado.
    if (items.has('preview')) items.remove('preview');
    if (items.has('submit')) {
      items.setContent('submit',
      // Sem o `App-primaryControl` do core: no celular essa classe leva o
      // botão para o cabeçalho e esconde o rótulo; aqui ele fica no
      // rodapé, em texto.
      m(Button, {
        className: "Button Button--primary CanelaComposer-submit",
        onclick: () => this.onsubmit()
      }, attrs.submitLabel));
    }
    const editing = app.composer.bodyMatches(EDIT);
    items.add('canelaCancel', m(Button, {
      className: "Button Button--link CanelaComposer-cancel",
      onclick: () => app.composer.close()
    }, editing ? t('composer.shell.cancel_edit') : t('composer.shell.discard')), -200);

    // Onde cabe a prévia ao lado (mesmo escondida pelo «), o "olho" do core
    // — que leva à prévia dentro do tópico — sobra.
    if (splitPossible() && items.has('preview')) items.remove('preview');
  });
}
export default function installComposerShell() {
  setPlaceholderProvider(bodyPlaceholder);
  installLabels();
  installHeights();
  installRoot();
  installHeader();
  installTagRequirements();
  installFooter();
  installToolbar();
  // Os modais de entrar/cadastrar (a casca de UI mora junto da do composer).
  installModals();
  // O modal de tags do flarum/tags no desenho do seletor de tags do composer.
  installTagModal();
  installAlignComposer();
  // No celular, o composer cabe acima do teclado (o do iOS cobre a página).
  installMobileViewport();
  // O menu do `@`/`#` do flarum/mentions no desenho do tema, e a medida do
  // cursor que ele usa sem recalcular a página inteira.
  installMentionsDropdown();
  installCaret();
}
flarum.reg.add('ramon-canela', 'forum/composer/installComposerShell', installComposerShell);